/**
 * AdvisorLearningRecommendations Component
 * 
 * Shows recommended training for advisors based on their performance patterns.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, ExternalLink, X, Check, Sparkles } from 'lucide-react';
import { useRoleTrainingRecommendations, useDismissRecommendation, useCompleteRecommendation } from '@/hooks/useRoleTrainingRecommendations';

interface AdvisorLearningRecommendationsProps {
  userId: string;
}

export function AdvisorLearningRecommendations({ userId }: AdvisorLearningRecommendationsProps) {
  const { data: recommendations, isLoading } = useRoleTrainingRecommendations(userId, 'advisor');
  const dismissMutation = useDismissRecommendation();
  const completeMutation = useCompleteRecommendation();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Learning Suggestions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-muted-foreground">
            Loading recommendations...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!recommendations || recommendations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Learning Suggestions
          </CardTitle>
          <CardDescription>
            Personalized training recommendations based on performance patterns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <Sparkles className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No recommendations at this time. Keep up the great work!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getCategoryLabel = (key: string) => {
    const labels: Record<string, string> = {
      'diagnostic_translation': 'Diagnostic Communication',
      'authorization_timing': 'Authorization Timing',
      'workflow_management': 'Workflow Management',
      'customer_communication': 'Customer Communication',
    };
    return labels[key] || key;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          Learning Suggestions
        </CardTitle>
        <CardDescription>
          Personalized training based on your performance patterns (advisory only)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {recommendations.map((rec) => (
            <div 
              key={rec.id} 
              className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs">
                    {getCategoryLabel(rec.categoryKey)}
                  </Badge>
                </div>
                
                {rec.artifact ? (
                  <div>
                    <h4 className="font-medium text-sm">{rec.artifact.title}</h4>
                    {rec.reason && (
                      <p className="text-xs text-muted-foreground mt-1">{rec.reason}</p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{rec.reason || 'Training recommendation'}</p>
                )}
              </div>

              <div className="flex items-center gap-1">
                {rec.artifact?.accessUrl && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    asChild
                  >
                    <a href={rec.artifact.accessUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-100"
                  onClick={() => completeMutation.mutate(rec.id)}
                  disabled={completeMutation.isPending}
                >
                  <Check className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => dismissMutation.mutate(rec.id)}
                  disabled={dismissMutation.isPending}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-muted-foreground mt-4 text-center">
          These are suggestions only — no mandatory training required.
        </p>
      </CardContent>
    </Card>
  );
}
